import { countBy, round, groupBy, isNil, uniqBy } from 'lodash'
import { isNotConform } from 'utils/QualityThresholdUtils'
import { getLabel } from 'utils/StoreUtils'
import { hasValue } from 'utils/NumberUtil'
import i18n from 'simple-react-i18n'
import { NATURES_MICROBIO, NATURES_PC } from '../components/AEPoverview/AEPoverviewPanel'

const calculateDataSample = (opeList, natures, groupAnalysis, parametersIndex) => {
    const { conform = 0, notConform = 0 } = countBy(opeList, operation => {
        const key = `${operation.qualitometer}:${operation.id}`
        const { [key]: analysisList = [] } = groupAnalysis
        const filteredAnalysis = analysisList.filter(a => {
            const param = parametersIndex[a.parameter]
            return param && natures.includes(parseInt(param.typeParam))
        })
        if (!filteredAnalysis.length) {
            return 'noData'
        }
        if (filteredAnalysis.some(a => isNotConform(a, a.threshold))) {
            return 'notConform'
        }
        return 'conform'
    })
    const total = conform + notConform
    return {
        nbTotal: total,
        nbNonConform: notConform,
        percent: total ? `${round(conform * 100 / total, 1)} %` : '0 %',
    }
}

const calculateDataAnalysis = (analysisList, natures, parametersIndex) => {
    const filteredAnalysis = analysisList.filter(a => {
        const param = parametersIndex[a.parameter]
        return param && natures.includes(parseInt(param.typeParam))
    })
    const { conform = 0, notConform = 0 } = countBy(filteredAnalysis, a => isNotConform(a, a.threshold) ? 'notConform' : 'conform')
    const total = conform + notConform
    return {
        nbTotal: total,
        nbNonConform: notConform,
        percent: total ? `${round(conform * 100 / total, 1)} %` : '0 %',
    }
}

const getExportRows = (base, microbioDataSample, microbioDataAnalysis, pcDataSample, pcDataAnalysis) => [{
    ...base,
    nbSample: { value: microbioDataSample.nbTotal, format: '0', cellType: 'number' },
    nonConformPLV: { value: microbioDataSample.nbNonConform, format: '0', cellType: 'number' },
    plvConformity: { value: microbioDataSample.percent, cellType: 'right' },
    nbAnalysis: { value: microbioDataAnalysis.nbTotal, format: '0', cellType: 'number' },
    nonConformAnalysis: { value: microbioDataAnalysis.nbNonConform, format: '0', cellType: 'number' },
    analysisConformity: { value: microbioDataAnalysis.percent, cellType: 'right' },
    nature: { value: i18n.microbiological },
}, {
    ...base,
    nbSample: { value: pcDataSample.nbTotal, format: '0', cellType: 'number' },
    nonConformPLV: { value: pcDataSample.nbNonConform, format: '0', cellType: 'number' },
    plvConformity: { value: pcDataSample.percent, cellType: 'right' },
    nbAnalysis: { value: pcDataAnalysis.nbTotal, format: '0', cellType: 'number' },
    nonConformAnalysis: { value: pcDataAnalysis.nbNonConform, format: '0', cellType: 'number' },
    analysisConformity: { value: pcDataAnalysis.percent, cellType: 'right' },
    nature: { value: i18n.physicochemic },
}]

// Bilan conformité calculé côté back (route distributionUnit/:id/conformity)
const DEFAULT_SUPPORT_VALUE = '-1'
const toInt = value => hasValue(value) ? parseInt(value) : undefined

// les écrans fonctionnent par année ; l'API accepte aussi startDate + endDate (prioritaires sur l'année)
export const getConformityFilterBody = (filter = {}) => ({
    year: toInt(filter.year),
    support: hasValue(filter.support) && `${filter.support}` !== DEFAULT_SUPPORT_VALUE ? `${filter.support}` : undefined,
    producer: toInt(filter.producer),
    referenceThreshold: toInt(filter.referenceThreshold),
    limitThreshold: toInt(filter.limitThreshold),
})

// { total, conforme, ref, lim } (répartition exclusive, lim prioritaire) -> taux de conformité
const formatConformityCount = ({ total = 0, conforme = 0, ref = 0, lim = 0 } = {}) => {
    return {
        nbTotal: total,
        conforme,
        ref,
        lim,
        percent: total ? `${round(conforme * 100 / total, 1)} %` : '0 %',
    }
}

export const CONFORMITY_COLORS = {
    nonConform: '#f44336',
    referenceExceeded: '#ff9800',
    conform: '#4caf50',
}

const EMPTY_COUNT = { total: 0, conforme: 0, ref: 0, lim: 0 }

const sumCounts = (counts = []) => counts.reduce((acc, count = {}) => ({
    total: acc.total + (count.total || 0),
    conforme: acc.conforme + (count.conforme || 0),
    ref: acc.ref + (count.ref || 0),
    lim: acc.lim + (count.lim || 0),
}), EMPTY_COUNT)

const formatPercent = percent => percent === 100 ? '100 %' : `${percent.toFixed(2).replace('.', ',')} %`

// statistiques d'une case du bilan : non conformités = dépassements de limite ; conformité = analyses sans dépassement de limite,
// en rouge si au moins une non conformité, en orange si au moins un dépassement de référence, en vert sinon
const getConformityStats = (sample = EMPTY_COUNT, analysis = EMPTY_COUNT) => {
    const percent = analysis.total ? (analysis.total - analysis.lim) * 100 / analysis.total : undefined
    const getColor = () => {
        if (!analysis.total) return undefined
        if (analysis.lim) return CONFORMITY_COLORS.nonConform
        if (analysis.ref) return CONFORMITY_COLORS.referenceExceeded
        return CONFORMITY_COLORS.conform
    }
    return {
        nbSample: sample.total,
        nbAnalysis: analysis.total,
        nonConform: analysis.lim,
        ref: analysis.ref,
        percent: isNil(percent) ? '-' : formatPercent(percent),
        color: getColor(),
    }
}

// lignes du tableau du bilan : par producteur (bactériologique, physico-chimique, total de la ligne) et ligne de total
export const getConformityOverviewRows = (result = {}) => {
    const producerResults = result.producers || []
    const natureCounts = (nature, type) => sumCounts(producerResults.map(p => p[nature]?.[type]))
    return {
        producers: producerResults.map(p => ({
            producer: p.producer,
            microbiological: getConformityStats(p.microbiological?.sample, p.microbiological?.analysis),
            physicochemical: getConformityStats(p.physicochemical?.sample, p.physicochemical?.analysis),
            total: getConformityStats(
                sumCounts([p.microbiological?.sample, p.physicochemical?.sample]),
                sumCounts([p.microbiological?.analysis, p.physicochemical?.analysis]),
            ),
        })),
        total: {
            microbiological: getConformityStats(natureCounts('microbiological', 'sample'), natureCounts('microbiological', 'analysis')),
            physicochemical: getConformityStats(natureCounts('physicochemical', 'sample'), natureCounts('physicochemical', 'analysis')),
            total: getConformityStats(
                sumCounts([natureCounts('microbiological', 'sample'), natureCounts('physicochemical', 'sample')]),
                sumCounts([natureCounts('microbiological', 'analysis'), natureCounts('physicochemical', 'analysis')]),
            ),
        },
    }
}

export const CONFORMITY_OVERVIEW_EXPORT_HEADERS = ['year', 'nature', 'producer', 'nbSample', 'conformSample', 'refSample', 'limSample', 'plvConformity', 'nbAnalysis', 'conformAnalysis', 'refAnalysis', 'limAnalysis', 'analysisConformity']

const toNumberCell = value => ({ value, format: '0', cellType: 'number' })

const getNatureExportRow = (base, nature, sample, analysis) => ({
    ...base,
    nature: { value: nature },
    nbSample: toNumberCell(sample.nbTotal),
    conformSample: toNumberCell(sample.conforme),
    refSample: toNumberCell(sample.ref),
    limSample: toNumberCell(sample.lim),
    plvConformity: { value: sample.percent, cellType: 'right' },
    nbAnalysis: toNumberCell(analysis.nbTotal),
    conformAnalysis: toNumberCell(analysis.conforme),
    refAnalysis: toNumberCell(analysis.ref),
    limAnalysis: toNumberCell(analysis.lim),
    analysisConformity: { value: analysis.percent, cellType: 'right' },
})

// lignes d'export du bilan calculé côté back (une ligne par producteur et par nature), sans en-têtes
export const getConformityOverviewExportRows = (result = {}, filter = {}, producers = []) => {
    return (result.producers || []).flatMap(p => {
        const base = {
            year: toNumberCell(filter.year),
            producer: { value: getLabel(producers, p.producer) || i18n.unknownProducer },
        }
        return [
            getNatureExportRow(base, i18n.microbiological, formatConformityCount(p.microbiological?.sample), formatConformityCount(p.microbiological?.analysis)),
            getNatureExportRow(base, i18n.physicochemic, formatConformityCount(p.physicochemical?.sample), formatConformityCount(p.physicochemical?.analysis)),
        ]
    })
}

export const getAEPoverviewExportData = ({
    analysis = [],
    operations = [],
    thresholdList = [],
    filter = {},
    producers = [],
    parametersIndex = {},
}) => {
    const formatedAnalysis = analysis.filter(a => thresholdList.some(t => !t.parameterCode || t.parameterCode === a.parameter))

    const producerIds = hasValue(filter.producer) ? [filter.producer] : uniqBy(analysis, 'producer').map(a => a.producer)

    const groupAnalysis = groupBy(formatedAnalysis, a => `${a.qualitometer}:${a.operation}`)
    const groupOperationsProducer = groupBy(operations, a => a.producer)

    return producerIds.flatMap(id => {
        const base = {
            producer: { value: getLabel(producers, id) || i18n.unknownProducer },
            year: { value: filter.year, format: '0', cellType: 'number' },
        }
        const { [id]: listOperations = [] } = groupOperationsProducer
        const listAnalysis = listOperations.flatMap(o => groupAnalysis[`${o.qualitometer}:${o.id}`] || [])

        const microbioDataSample = calculateDataSample(listOperations, NATURES_MICROBIO, groupAnalysis, parametersIndex)
        const microbioDataAnalysis = calculateDataAnalysis(listAnalysis, NATURES_MICROBIO, parametersIndex)

        const pcDataSample = calculateDataSample(listOperations, NATURES_PC, groupAnalysis, parametersIndex)
        const pcDataAnalysis = calculateDataAnalysis(listAnalysis, NATURES_PC, parametersIndex)

        return getExportRows(base, microbioDataSample, microbioDataAnalysis, pcDataSample, pcDataAnalysis)
    })
}

export {
    calculateDataSample,
    calculateDataAnalysis,
}
